import { ComponentIds, HierarchyUtils } from '@inter-ikea-core/core';
import {
  CameraBehaviorComponent,
  directions,
  FocusOptions,
  orbitLimits as limits,
  paddingMobile,
  PartKey
} from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { getActiveWidget } from 'ducks/mobile';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isMobile } from 'utils/platform';
import getCameraConfig from './eketCameraConfig';

/**
 * This effect will update the cameras focus direction
 * based on which tab is active in product menus
 */
const useMobileCameraBehavior = () => {
  const coreApi = useCore();
  const activeWidget = useSelector(getActiveWidget);

  useEffect(() => {
    if (!coreApi.camera?.getCameraEntity) {
      return;
    }

    const cameraEntity = coreApi.camera.getCameraEntity();

    if (!cameraEntity) return;

    const component = cameraEntity.getComponent(
      CameraBehaviorComponent.id
    ) as CameraBehaviorComponent;
    const prevOptions = component.options.focusOptions;

    const frames = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

    const frameHierarchy = frames.flatMap(frame => {
      const framePart = coreApi.entities.findEntityWithPartKey(
        HierarchyUtils.children(frame),
        PartKey.FRAME
      );

      return framePart?.id;
    });

    const frameEntityIds = frames.map(f => f.id);
    const legIds = coreApi.entities.getByComponent(ComponentIds.LEG_COMPONENT_ID).map(e => e.id);

    let entitiesToFocusOnIds = [...frameHierarchy, frameEntityIds, ...legIds]
      .flat()
      .filter(Boolean) as any;

    if (!activeWidget) {
      const direction = directions.FRONT_ANGLED_ABOVE_RIGHT_SMALL as any;
      const orbitLimits = limits.FRONT as any;

      const focusOptions = {
        ...prevOptions,
        direction,
        orbitLimits,
        padding: paddingMobile
      } as FocusOptions;

      if (entitiesToFocusOnIds.length > 0) {
        cameraEntity.addComponent(
          new CameraBehaviorComponent({
            focusOptions,
            entityIds: entitiesToFocusOnIds
          })
        );
      }
      return;
    }

    const { direction, orbitLimits } = getCameraConfig(activeWidget);

    // if legs menu selected focus on the legs
    if (activeWidget === 'legs-product-list') {
      entitiesToFocusOnIds = legIds;

      const padding = {
        ...paddingMobile,
        bottom: 40
      };

      const focusOptions = {
        ...prevOptions,
        direction,
        orbitLimits,
        padding
      } as FocusOptions;

      cameraEntity.addComponent(
        new CameraBehaviorComponent({ focusOptions, entityIds: entitiesToFocusOnIds })
      );
    } else {
      const focusOptions = {
        ...prevOptions,
        direction,
        orbitLimits,
        padding: paddingMobile
      } as FocusOptions;

      cameraEntity.addComponent(
        new CameraBehaviorComponent({ focusOptions, entityIds: entitiesToFocusOnIds })
      );
    }
  }, [activeWidget, coreApi]);
};

const MobileCameraBehavior = () => {
  useMobileCameraBehavior();
  return <></>;
};

const EketCameraBehavior = () => {
  if (isMobile()) {
    return <MobileCameraBehavior />;
  }
  return <></>;
};

export default EketCameraBehavior;
